/**
 * Audience-builder API client: facet-population counts for the rail badges,
 * the industries-within-a-relationship breakdown, contact-group (saved
 * segment) options, the live server-resolved dedup preview, and the
 * paginated people list matching the current facet selection.
 *
 * Talks to crm-api exclusively through the shared axios instance
 * (`@crm-src/lib/api`) — never raw `fetch` — so auth headers, the `/api/v1`
 * prefix, and the short-TTL CRM-session re-mint on 401 all apply uniformly
 * (see `crm/lib/api.ts`). Array-valued params (`relationship`, `industry`)
 * are passed as plain arrays in `params`; axios's default `AxiosURLSearchParams`
 * serializer renders these as repeated bracket keys — e.g.
 * `relationship[]=customer&relationship[]=standalone` — which is exactly the
 * shape Rails' `params[:relationship]` (an Array) expects. No custom
 * `paramsSerializer` needed.
 *
 * Backend contract (crm-api):
 *   - `GET /contacts` — `relationship[]`, `industry[]`, `page`, `per_page`.
 *   - `GET /contacts/industries` — `relationship[]`.
 *   - `POST /campaigns/audience_preview` — `{ target_selection }`.
 *   - `GET /contact_lists` (via `ContactListsAPI.getAll`) — contact groups.
 * All six relationship populations resolve through the `/contacts` directory —
 * the SAME mapping the send-time resolver uses — so the rail badges count the
 * PEOPLE a facet actually contributes (not the number of parent companies) and
 * can never disagree with the live preview / actual send.
 */
import api from "@crm-src/lib/api"
import { ContactListsAPI } from "@crm-src/lib/contact-lists-api"

import type { RelationshipFacet } from "./facets"
import { RELATIONSHIP_FACETS } from "./facets"
import type { EmailRecipient, TargetSelection } from "../email-target-selector"
import { contactRecipients } from "../email-target-selector/recipients"
import { sanitizeTargetSelection } from "../sanitize-target-selection"

/** Pagination total from the `/contacts` envelope (`meta.pagination.total_count`). */
function contactsTotal(data: unknown): number {
  const d = data as { meta?: { pagination?: { total_count?: number } } } | undefined
  return d?.meta?.pagination?.total_count ?? 0
}

/**
 * One count per `RelationshipFacet` population, for the facet-rail badges. Every
 * facet is counted uniformly via `GET /contacts?relationship[]=<facet>&per_page=1`
 * (only the pagination total is paid for, not the rows), so each badge is the
 * number of directory PEOPLE that facet resolves to — consistent with the live
 * preview and the actual send.
 */
export async function fetchRelationshipCounts(): Promise<Record<RelationshipFacet, number>> {
  const results = await Promise.all(
    RELATIONSHIP_FACETS.map((facet) =>
      api.get("/contacts", { params: { per_page: 1, relationship: [facet] } }),
    ),
  )

  const counts = {} as Record<RelationshipFacet, number>
  RELATIONSHIP_FACETS.forEach((facet, i) => {
    counts[facet] = contactsTotal(results[i]?.data)
  })
  return counts
}

/** Industries (+ counts) reachable across the given relationship facets. With no
 * relationship selected, counts span ALL populations (the merged directory) so the
 * default industry breakdown is the full universe — matching what selecting an
 * industry alone actually sends to — not just the qualified subset. */
export async function fetchIndustries(
  rels: RelationshipFacet[],
): Promise<{ industry: string; count: number }[]> {
  const effective = rels.length > 0 ? rels : RELATIONSHIP_FACETS
  const response = await api.get("/contacts/industries", { params: { relationship: effective } })
  return response.data?.data ?? []
}

export interface ContactGroupOption {
  id: number
  name: string
  count: number
}

/** Active contact groups (saved segments) for the Contact Groups facet. */
export async function fetchContactGroups(): Promise<ContactGroupOption[]> {
  const response = await ContactListsAPI.getAll({ active: true, per_page: 100, sort: "name" })
  const lists = response.data ?? []
  return lists.map((list) => ({
    id: list.id,
    name: list.name,
    count: list.members_count ?? list.contacts_count ?? 0,
  }))
}

/** Live server-resolved dedup preview for a (not-yet-saved) target selection. */
export async function fetchAudiencePreview(
  sel: TargetSelection,
): Promise<{ total: number; deduped: number; duplicates: number }> {
  const response = await api.post("/campaigns/audience_preview", {
    target_selection: sanitizeTargetSelection(sel),
  })
  const data = response.data ?? {}
  return {
    total: data.total ?? 0,
    deduped: data.deduped ?? 0,
    duplicates: data.duplicates ?? 0,
  }
}

/**
 * One page of directory people for the results list.
 * - With a `search` query: a GLOBAL find across ALL populations (so you can find
 *   and add anyone, regardless of the current facet selection).
 * - Otherwise: the current selection's pool — the relationship facets UNIONed with
 *   any selected groups' members, narrowed by industry. Nothing selected (or an
 *   industry alone) spans all populations so the list matches the merged counts.
 */
export async function fetchPeople(
  rels: RelationshipFacet[],
  industries: string[],
  groups: number[],
  search: string,
  page: number,
): Promise<{ people: EmailRecipient[]; total: number }> {
  const params: Record<string, unknown> = { page, per_page: 25 }
  const trimmed = search.trim()

  if (trimmed) {
    params.search = trimmed
    params.relationship = RELATIONSHIP_FACETS
  } else if (rels.length > 0) {
    params.relationship = rels
    if (industries.length > 0) params.industry = industries
    if (groups.length > 0) params.group_ids = groups
  } else if (groups.length > 0 && industries.length === 0) {
    // Only groups selected → list exactly those groups' members.
    params.group_ids = groups
  } else {
    // Nothing selected, or an industry alone → span all populations.
    params.relationship = RELATIONSHIP_FACETS
    if (industries.length > 0) params.industry = industries
  }

  const response = await api.get("/contacts", { params })
  const rows = response.data?.data ?? []
  const total = contactsTotal(response.data)

  return { people: contactRecipients(rows), total }
}
